import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useMemo,
	useState,
	Dispatch,
	SetStateAction
} from "react";
import { localize, } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	Anchor,
	FormattedText,
	Dialog,
	Toolbar,
	ActionListItem,
	AlertBar,
	Space,
	SelectOption
} from "client/Components";
import {
	acceptAppointment,
	AppointmentDetailViewModel,
	AppointmentResponseKind,
	AppointmentResponseStatus,
	AttendeeModel,
	declineAppointment,
	getAppointmentDetails,
	OrganizerModel,
	RecurringAppointmentOperationKind
} from "client/Api/Calendar/calendar";
import { AppointmentDetailsRow } from "./AppointmentDetailsRow";
import { OnlineMeetingButton } from "../OnlineMeetingButton";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useStableImperativeCallback,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import styles from "./AppointmentDetailsDialog.module.less";
import { navigateToOwaAppointment } from "client/Features/Email/OwaNavigation/owaNavigator";
import { AppointmentSpan } from "../AppointmentSpan";
import { TimeZone } from "client/DateAndTime/timezones";
import { never } from "client_legacy/ndaw/errors";
import {
	CalendarEvent,
	publish,
	RespondId
} from "../events";
import { formatCalendarLocations } from "../calendarUtils";
import { DeleteOrCancelButton } from "../DeleteOrCancelButton";
import { ComposeAppointmentDialog, defaultReminderOptions } from "../ComposeAppointmentDialog/ComposeAppointmentDialog";
import { Body, BodyHandle } from "client/Features/Email/EmailContent/EmailViewer/Body/Body";
import { AppointmentAttendeesList } from "./AppointmentAttendeesList";
import classNames from "classnames";
import { getDisplayFriendlyDelay } from "client/DateAndTime/culture";
import { computeShouldShowEditButton } from "../CalendarContent/ViewAppointmentBox/ViewAppointmentBox";
import {
	AppointmentInformation,
	AppointmentOrIdentifier,
	DetailsAppointmentInformation,
	isAppointmentInformation
} from "../common";

export type AppointmentDetailsDialogProps = {
	appointmentOrIdentifier: AppointmentOrIdentifier;
	onClose(): void;
	onAfterMutatingAppointment?: (calendarIds: readonly string[]) => void;
};

export const AppointmentDetailsDialog = (props: AppointmentDetailsDialogProps): ReactElement => {
	const {
		appointmentOrIdentifier,
		onClose: onCloseFromProps,
		onAfterMutatingAppointment
	} = props;

	const [appointment, setAppointment] = useState<AppointmentInformation | null>(null);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [composeDialogState, setComposeDialogState] = useState<ComposeDialogState>({ isOpen: false });
	const [shouldShowInlineImages, setShouldShowInlineImages] = useState<boolean>(false);

	const userTimezone: TimeZone = useUserTimezone();
	const alertManager: AlertManager = useAlerts();
	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	useEffect(
		(): (() => void) | void => {
			if (isAppointmentInformation(appointmentOrIdentifier)) {
				setAppointment(appointmentOrIdentifier);
				return;
			}

			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					const viewModel: AppointmentDetailViewModel = await getAppointmentDetails(
						appointmentOrIdentifier,
						signal
					);

					setAppointment({
						...viewModel.model,
						kind: "details"
					});
					setShouldShowInlineImages(viewModel.shouldAutomaticallyShowSenderImages);

					setIsBusy(false);
				} catch (e) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			appointmentOrIdentifier,
			onClose
		]
	);

	const trackingId: string = "appointment-details-dialog";

	const renderContent = (): ReactElement | null => {
		if (!appointment) {
			return null;
		}

		switch(appointment.kind) {
			case "basic":
				return (
					<Dialog.Body goesEdgeToEdge>
						<div className={styles.dialogBodyContainer}>
							<div className={styles.leftColumn}>
								<Dialog.Card>
									<AppointmentDetailsRow icon="clock">
										<span>
											<AppointmentSpan
												appointment={appointment}
												timezone={userTimezone}
											/>
										</span>
										<br />
									</AppointmentDetailsRow>
								</Dialog.Card>
							</div>
						</div>
					</Dialog.Body>
				);
			case "details":
				return (
					<Details
						appointment={appointment}
						onClose={onClose}
						composeDialogState={composeDialogState}
						onComposeDialogStateChange={setComposeDialogState}
						onIsBusyChange={setIsBusy}
						setAppointment={setAppointment}
						userTimezone={userTimezone}
						onAfterMutatingAppointment={onAfterMutatingAppointment}
						shouldShowInlineImages={shouldShowInlineImages}
						onShouldShowInlineImagesChange={setShouldShowInlineImages}
						trackingId={trackingId}
					/>
				);
			default:
				never(appointment);
		}
	};

	return (
		<>
			<Dialog
				hasBusyIndicator={isBusy}
				trackingId={trackingId}
				variant="left-slider"
				onClose={onClose}
				backdrop={composeDialogState.isOpen ? "invisible" : "visible"}
			>
				{!appointment
					? (
						<Dialog.Body>
							<div className={styles.busyIndicatorHeightReservation} />
						</Dialog.Body>
					)
					: (
						<>
							<Dialog.Header
								title={appointment.subject || localize("_NoSubject")}
								onClose={onClose}
								hasBorder
							/>
							{renderContent()}
						</>
					)
				}
			</Dialog>
		</>
	);
};
AppointmentDetailsDialog.displayName = "AppointmentDetailsDialog";

type ComposeDialogState = {
	isOpen: false;
} | {
	isOpen: true;
	recurringOperationKind?: RecurringAppointmentOperationKind;
};

type DetailsProps = {
	appointment: DetailsAppointmentInformation;
	setAppointment: Dispatch<SetStateAction<AppointmentInformation | null>>;
	userTimezone: TimeZone;
	shouldShowInlineImages: boolean;
	onShouldShowInlineImagesChange: (status: boolean) => void;
	composeDialogState: ComposeDialogState;
	onComposeDialogStateChange: (state: ComposeDialogState) => void;
	onIsBusyChange: (isBusy: boolean) => void;
	onClose: () => void;
	onAfterMutatingAppointment?: (calendarIds: readonly string[]) => void;
	trackingId: string;
};

const Details = (props: DetailsProps): ReactElement => {
	const {
		appointment,
		composeDialogState,
		userTimezone,
		trackingId,
		shouldShowInlineImages,
		onIsBusyChange,
		onClose,
		onAfterMutatingAppointment,
		onComposeDialogStateChange,
		setAppointment,
		onShouldShowInlineImagesChange
	} = props;

	const [shouldAllowUnsecureContent, setShouldAllowUnsecureContent] = useState<boolean>(false);
	const [hasUnsecureContent, setHasUnsecureContent] = useState<boolean>(false);
	const [hasInlineImages, setHasInlineImages] = useState<boolean>(false);

	const isBigViewport: boolean = useWorkspaceMediaQuery(MediaQueryKind.CompactTablet);
	const bodyRef: RefObject<BodyHandle | null> = useRef<BodyHandle>(null);
	const alertManager: AlertManager = useAlerts();

	const myResponseStatus: AppointmentResponseStatus | null = appointment.myResponseStatus ?? null;
	const isResponseOutstanding: boolean = myResponseStatus?.isResponseOutstanding ?? false;

	const canRespond: boolean = !appointment.isCancelled
		&& !appointment.isOrganizer
		&& appointment.calendar
			.canEdit;

	const unsecureContentHandler = useCallback(
		(): void => {
			setHasUnsecureContent(true);
		},
		[]
	);

	const inlineImagesDetectedHandler = useCallback(
		(): void => {
			setHasInlineImages(true);
		},
		[]
	);

	const allowInlineImagesHandler = useCallback(
		(): void => {
			if (!bodyRef.current) {
				console.error("Description body ref is not set");
				return;
			}

			onIsBusyChange(true);
			bodyRef.current
				.loadInlineImages()
				.finally(() => {
					onIsBusyChange(false);
					onShouldShowInlineImagesChange(true);
				});

			// After 10 seconds of loading we hide the busy indicator to prevent endless loading
			// in the scenario exchange is slow
			setTimeout(
				() => onIsBusyChange(false),
				10000
			);
		},
		[onIsBusyChange, onShouldShowInlineImagesChange]
	);

	const formattedAppointmentLocation = useMemo(
		(): string | null => {
			return formatCalendarLocations(
				appointment.locations
					.map(x => x.displayName) ?? []
			);
		},
		[appointment.locations]
	);

	const deleteOrCancelSuccessHandler = useCallback(
		(): void => {
			onAfterMutatingAppointment?.([appointment.calendar.id]);
			onClose();
		},
		[
			appointment,
			onAfterMutatingAppointment,
			onClose
		]
	);

	const shouldShowEditButton = useMemo(
		(): boolean => {
			return computeShouldShowEditButton(appointment);
		},
		[appointment]
	);

	const editAppointmentHandler = useCallback(
		(recurringOperationKind?: RecurringAppointmentOperationKind): void => {
			onComposeDialogStateChange({
				isOpen: true,
				recurringOperationKind
			});
		},
		[onComposeDialogStateChange]
	);

	const closeComposeAppointmentDialogHandler = useCallback(
		(savedCalendarIds?: readonly string[]): void => {
			onComposeDialogStateChange({ isOpen: false });

			if (isNotNil(savedCalendarIds)) {
				onAfterMutatingAppointment?.(savedCalendarIds);
			}

			onClose();
		},
		[
			onComposeDialogStateChange,
			onAfterMutatingAppointment,
			onClose
		]
	);

	const onOpenInOwa = (id: string): void => {
		navigateToOwaAppointment(id, true);
		onClose();
	};

	const respondHandler = useCallback(
		async (
			appointment: DetailsAppointmentInformation,
			responseKind: ResponseKind
		): Promise<void> => {
			try {
				onIsBusyChange(true);

				const respondId: RespondId = appointment.masterId
					? { master: appointment.masterId }
					: { appointment: appointment.id };

				await sendAppointmentResponse(
					respondId,
					appointment.calendar
						.id,
					responseKind,
					alertManager
				);

				switch (responseKind) {
					case "accept":
						setAppointment(
							(x): AppointmentInformation | null => {
								if (!x || x.kind === "basic") {
									return x;
								}

								return {
									...x,
									myResponseStatus: {
										isResponseOutstanding: false,
										responseKind: AppointmentResponseKind.Accept
									}
								};
							}
						);

						onIsBusyChange(false);
						break;
					case "decline":
						onClose();
						break;
					default:
						never(responseKind);
				}
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				onIsBusyChange(false);
			}
		},
		[
			alertManager,
			onClose,
			onIsBusyChange,
			setAppointment
		]
	);

	const attendees: readonly AttendeeModel[] = appointment.attendees ?? [];

	const organizer: OrganizerModel | nil = appointment.organizer;
	const { emailAddress: currentUserEmailAddress } = userConfiguration();
	const isCurrentUserTheOrganizer: boolean = organizer && currentUserEmailAddress
		? organizer.emailAddress.toUpperCase() === currentUserEmailAddress.toUpperCase()
		: false;

	const showAttendees: boolean = attendees.length > 0 || !isCurrentUserTheOrganizer;
	const shouldSplitColumns: boolean = isBigViewport && showAttendees;

	return (
		<>
			<Dialog.Body goesEdgeToEdge>
				<div className={
					classNames(
						styles.dialogBodyContainer,
						{
							[styles.dialogBodyContainer_split]: shouldSplitColumns
						}
					)}
				>
					<div className={styles.leftColumn}>
						<Dialog.Card>
							<AppointmentDetailsRow icon="clock">
								<span>
									<AppointmentSpan
										appointment={appointment}
										timezone={userTimezone}
									/>
								</span>
								<br />
							</AppointmentDetailsRow>
							{(isNotNil(formattedAppointmentLocation) || appointment.meeting) && (
								<AppointmentDetailsRow icon="location-dot">
									{appointment.locations.length > 0 && (
										<>
											<span>
												{formattedAppointmentLocation}
											</span>
										</>
									)}
									{appointment.meeting && (
										<div className={styles.buttonContainer}>
											<OnlineMeetingButton
												{...appointment.meeting}
												trackingId={generateTrackingId(trackingId, "__join-meeting")}
											/>
										</div>
									)}
								</AppointmentDetailsRow>
							)}

							<AppointmentDetailsRow icon="calendar">
								<span>{appointment.calendar.name}</span>
							</AppointmentDetailsRow>

							{isNotNil(appointment.reminderBeforeInMinutes) && (
								<AppointmentDetailsRow icon="bell">
									<ReminderText durationInMinutes={appointment.reminderBeforeInMinutes} />
								</AppointmentDetailsRow>
							)}
						</Dialog.Card>

						{!shouldSplitColumns && showAttendees && (
							<Dialog.Card>
								<AppointmentAttendeesList
									attendees={attendees}
									organizer={appointment.organizer}
									variant="compact"
								/>
							</Dialog.Card>
						)}

						{hasUnsecureContent && !shouldAllowUnsecureContent && (
							<div className={styles.alertContainer}>
								<AlertBar
									kind="info"
									isInline
									isDismissable={false}
									trackingId="insecure-content-alert"
								>
									<span>
										<span>{localize("_UnsecureContentIsHiddenMessage")}</span>
										<Space />
										<Anchor
											onClick={() => setShouldAllowUnsecureContent(true)}
											trackingId="allow"
										>
											{localize("_ClickHereToShowBlockedContent")}
										</Anchor>
									</span>
								</AlertBar>
							</div>
						)}

						{isNotNilOrWhiteSpace(appointment.description) && isNotNil(appointment.descriptionKind) && (
							<Dialog.Card>
								<AppointmentDetailsRow icon="align-left">
									{hasInlineImages && !shouldShowInlineImages && (
										<div className={styles.enableInlineImagesContainer}>
											<Anchor onClick={allowInlineImagesHandler} trackingId="show-inline-images">
												{localize("_ClickHereToShowImagesInTheDescription")}
											</Anchor>
										</div>
									)}
									<Body
										ref={bodyRef}
										body={appointment.description}
										bodyKind={appointment.descriptionKind}
										shouldAllowUnsecureContent={shouldAllowUnsecureContent}
										shouldAutomaticallyShowSenderImages={shouldShowInlineImages}
										onInlineImagesDetected={inlineImagesDetectedHandler}
										onUnsecureContent={unsecureContentHandler}
									/>
								</AppointmentDetailsRow>
							</Dialog.Card>
						)}

						{canRespond && isResponseOutstanding && (
							<Dialog.Card>
								<AppointmentDetailsRow icon="calendar-update">
									{appointment.organizer && (
										<span>
											<FormattedText
												text={localize("_NameInvitedYouToThisEvent")}
												thingsToReplace={[
													{
														what: "{{Name}}",
														filler: appointment.organizer
															.displayName
													}
												]}
											/>
										</span>
									)}

									<div className={styles.buttonContainer}>
										<Toolbar>
											<Toolbar.Button
												kind="secondary"
												text={localize("_Accept")}
												onClick={() => respondHandler(appointment, "accept")}
												icon="check"
												trackingId="accept"
											/>
											<Toolbar.Button
												kind="secondary"
												text={localize("_Decline")}
												onClick={() => respondHandler(appointment, "decline")}
												icon="xmark"
												trackingId="decline"
											/>
										</Toolbar>
									</div>
								</AppointmentDetailsRow>
							</Dialog.Card>
						)}
					</div>

					{shouldSplitColumns && (
						<div className={styles.rightColumn}>
							<AppointmentAttendeesList
								attendees={attendees}
								organizer={appointment.organizer}
								variant="full"
							/>
						</div>
					)}

				</div>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						text={{
							template: localize("_OpenInAppName"),
							replacements: [{ what: "{0}", filler: localize("_OutlookWebApp") }]
						}}
						onClick={() => onOpenInOwa(ensureNotNil(appointment.id))}
						trackingId="open-in-owa"
					/>
					{shouldShowEditButton && (
						<EditAppointmentButton
							isSeries={isNotNil(appointment.masterId)}
							onEdit={editAppointmentHandler}
						/>
					)}
					<DeleteOrCancelButton
						appointmentDetailsView={appointment}
						onIsBusyChange={onIsBusyChange}
						onSuccess={deleteOrCancelSuccessHandler}
					/>
				</Toolbar>
			</Dialog.Footer>
			{composeDialogState.isOpen && (
				<ComposeAppointmentDialog
					timezone={userTimezone}
					appointmentIdentifier={{
						id: appointment.id,
						calendarId: appointment.calendar.id
					}}
					recurringOperationKind={composeDialogState.recurringOperationKind}
					onClose={closeComposeAppointmentDialogHandler}
				/>
			)}
		</>
	);
};
Details.displayName = "Details";

type EditAppointmentButtonProps = {
	isSeries: boolean;
	onEdit: (recurringAppointmentOperationKind?: RecurringAppointmentOperationKind) => void;
};

const EditAppointmentButton = (props: EditAppointmentButtonProps): ReactElement => {
	const {
		isSeries,
		onEdit
	} = props;

	return isSeries ? (
		<Toolbar.MenuButton
			kind="secondary"
			text={localize("_Edit")}
			rightIcon="chevron-down"
			trackingId="edit-button"
		>
			<ActionListItem
				text={localize("_ThisEventOnly")}
				onClick={() => onEdit(RecurringAppointmentOperationKind.Current)}
				trackingId={generateTrackingId("edit-button", "|event-only")}
			/>
			<ActionListItem
				text={localize("_ThisAndAllFollowingEvents")}
				onClick={() => onEdit(RecurringAppointmentOperationKind.CurrentAndFollowing)}
				trackingId={generateTrackingId("edit-button", "|event-and-following")}
			/>
			<ActionListItem
				text={localize("_AllEventsInTheSeries")}
				onClick={() => onEdit(RecurringAppointmentOperationKind.All)}
				trackingId={generateTrackingId("edit-button", "|all-events")}
			/>
		</Toolbar.MenuButton>
	) : (
		<Toolbar.Button
			kind="secondary"
			text={localize("_Edit")}
			onClick={() => onEdit(undefined)}
			trackingId="edit-button"
		/>
	);
};
EditAppointmentButton.displayName = "EditAppointmentButton";

export type ResponseKind = "accept" | "decline";

export type ReminderTextProps = {
	durationInMinutes: number;
};

export const ReminderText = (props: ReminderTextProps): ReactElement => {
	const {
		durationInMinutes
	} = props;

	const durationText = useMemo(
		(): string => {
			const defaultOption: SelectOption<number | null> | undefined = defaultReminderOptions
				.find((x) => x.value === durationInMinutes);

			if (defaultOption) {
				return defaultOption.text;
			}

			return getDisplayFriendlyDelay(durationInMinutes);
		},
		[durationInMinutes]
	);

	return (
		<span>
			{durationInMinutes === 0
				? durationText
				: (
					<FormattedText
						text={localize("_DurationBefore")}
						thingsToReplace={[
							{ what: "{{Duration}}", filler: durationText }
						]}
					/>
				)
			}
		</span>
	);
};
ReminderText.displayName = "ReminderText";

export async function sendAppointmentResponse(
	id: RespondId,
	calendarId: string,
	responseKind: ResponseKind,
	alertManager: AlertManager
): Promise<void> {
	const idToSend: string = "master" in id
		? id.master
		: id.appointment;

	switch (responseKind) {
		case "accept":
			await acceptAppointment({
				id: idToSend,
				calendarId
			});

			alertManager.showSuccess(localize("_YouRespondedYes"));

			publish(
				CalendarEvent.Responded,
				{
					id,
					response: AppointmentResponseKind.Accept
				}
			);
			break;
		case "decline":
			await declineAppointment({
				id: idToSend,
				calendarId
			});

			alertManager.showSuccess(localize("_YouRespondedNo"));

			publish(
				CalendarEvent.Responded,
				{
					id,
					response: AppointmentResponseKind.Decline
				}
			);
			break;
		default:
			never(responseKind);
	}
}

export type AppointmentDetailsDialogData = Omit<AppointmentDetailsDialogProps, "onClose">;

export function useAppointmentDetailsDialog(): [
	element: ReactElement | null,
	open: (data: AppointmentDetailsDialogData) => void
] {
	const [data, setData] = useState<AppointmentDetailsDialogData | null>(null);

	const openHandler = useCallback(
		(data: AppointmentDetailsDialogData): void => {
			setData(data);
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			setData(null);
		},
		[]
	);

	const renderDialog = (): ReactElement | null => {
		if (!data) {
			return null;
		}

		return (
			<AppointmentDetailsDialog
				{...data}
				onClose={closeHandler}
			/>
		);
	};

	return [
		renderDialog(),
		openHandler
	];
}
